'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AccordionItem {
  question?: string;
  answer?: string;
  title?: string;
  content?: string;
  id?: string;
}

interface AccordionProps {
  items: AccordionItem[];
  singleOpen?: boolean;
  className?: string;
}

export function Accordion({ items, singleOpen = false, className }: AccordionProps) {
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());

  const toggleOpen = (id: string) => {
    const newOpen = new Set(openIds);
    if (newOpen.has(id)) {
      newOpen.delete(id);
    } else {
      if (singleOpen) {
        newOpen.clear();
      }
      newOpen.add(id);
    }
    setOpenIds(newOpen);
  };

  return (
    <div className={cn('space-y-3', className)}>
      {items.map((item, index) => {
        const id = item.id || `accordion-${index}`;
        const question = item.question ?? item.title ?? '';
        const answer = item.answer ?? item.content ?? '';
        const isOpen = openIds.has(id);

        return (
          <motion.div
            key={id}
            className="border border-border rounded-sm overflow-hidden"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <button
              onClick={() => toggleOpen(id)}
              className="w-full px-4 py-4 flex items-center justify-between hover:bg-surface transition-colors duration-200"
            >
              <span className="text-left font-medium text-foreground">{question}</span>
              <motion.div
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="flex-shrink-0 ml-3"
              >
                <ChevronDown className="w-5 h-5 text-primary" />
              </motion.div>
            </button>

            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div className="px-4 py-4 bg-surface text-foreground border-t border-border">
                    {answer}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
}
